import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { rtlLangs } from '../../translations/rtl-langs';
import { isNewSeatmapDesign } from '../../utils/seatmapDesign';
import { resolveAirlineLogo } from '../../utils/logos';

export const AirlineComponent = ({
  currentSeatmap,
  flights = [],
  locale,
  theme = {},
  useNewDesign = false,
}) => {
  const currentFlight = useMemo(() => {
    const [flight = {}] = flights.filter(
      (f) => f.segment_id === currentSeatmap
    );
    return flight;
  }, [flights, currentSeatmap]);

  const { airline, flightNumber, airlineLogo } = useMemo(() => {
    const allOperatingFlightInfoIsAvailable = Boolean(
      currentFlight.operating_airline &&
        currentFlight.operating_flight_number &&
        currentFlight.operating_airline_logo
    );

    if (allOperatingFlightInfoIsAvailable) {
      return {
        airline: currentFlight.operating_airline,
        flightNumber: currentFlight.operating_flight_number,
        airlineLogo: currentFlight.operating_airline_logo,
      };
    } else {
      return {
        airline: currentFlight.marketing_airline,
        flightNumber: currentFlight.marketing_flight_number,
        airlineLogo: currentFlight.marketing_airline_logo,
      };
    }
  }, [currentFlight]);

  // `airline` is the IATA code (e.g. "CQ"), which is what theme.logos keys on.
  // Null means the partner hid logos. An empty or missing value still renders,
  // because the element has to keep its box the way it always has.
  const resolvedLogo = useMemo(
    () => resolveAirlineLogo(theme, airline, airlineLogo),
    [theme, airline, airlineLogo]
  );

  const renderAirlineInfo = () => (
    <div
      className="gr-hidden md:gr-flex sm:p-6 gr-text-sm"
      style={{ minWidth: 115 }} // Prevent text from wrapping to more 3 lines
    >
      {resolvedLogo !== null && (
        <img
          className="gr-h-12 gr-w-12 gr-rounded-full gr-object-contain gordian-logo-classic"
          alt={airline}
          aria-label={airline ? `${airline} ${flightNumber}` : 'Airline logo'}
          src={resolvedLogo}
        />
      )}
      <div className="gr-block gr-text-left gr-ml-2 gr-mt-1">
        {airline && (
          <h2 className="gr-text-sm gr-text-title">
            {airline}
            {flightNumber && ` ${flightNumber}`}
          </h2>
        )}
        {airline && (
          <div
            className="operated_by gr-text-secondary-text gr-text-sm"
            dir={rtlLangs.includes(locale) ? 'rtl' : 'ltr'}
          >
            <FormattedMessage id="operated_by" defaultMessage="Operated by" />:{' '}
            {airline}
          </div>
        )}
      </div>
    </div>
  );

  const renderNewAirlineInfo = () => (
    <div
      className="gr-hidden md:gr-flex gr-items-center gr-gap-2 gr-text-sm"
      style={{ minWidth: 115 }}
    >
      {resolvedLogo !== null && (
        <img
          className="gr-h-8 gr-w-8 gr-rounded-md gr-object-contain gordian-logo-redesign"
          alt={airline}
          aria-label={airline ? `${airline} ${flightNumber}` : 'Airline logo'}
          src={resolvedLogo}
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      )}
      {airline && (
        <span className="gr-text-sm gr-font-medium gr-text-gray-700">
          {airline}
          {flightNumber && ` ${flightNumber}`}
        </span>
      )}
    </div>
  );

  return useNewDesign ? renderNewAirlineInfo() : renderAirlineInfo();
};

const mapStateToProps = (state) => ({
  flights: state.itinerary.flights,
  currentSeatmap: state.itinerary.currentSeatmap,
  locale: state.session.locale,
  theme: state.session.theme,
  useNewDesign: isNewSeatmapDesign(state.session.variants),
});

const mapDispatchToProps = {};

// Connect the functional component
export default connect(mapStateToProps, mapDispatchToProps)(AirlineComponent);
